import React from "react";
import { motion } from "framer-motion";
import { Search, ClipboardCheck, ArrowRight, CalendarCheck } from "lucide-react";

const CALENDLY = "https://calendly.com/wordgumbolearningco";

const CARDS = [
  {
    key: "screener",
    icon: Search,
    name: "Dyslexia Screener",
    accent: "from-wg-orange to-wg-yellow",
    chip: "bg-wg-orange",
    points: [
      "A helpful starting point if you suspect reading difficulty",
      "Quick, parent-friendly insights",
      "Guidance on next steps and whether further evaluation may help",
    ],
  },
  {
    key: "core",
    icon: ClipboardCheck,
    name: "Full Dyslexia Core Assessment",
    accent: "from-wg-teal to-wg-green",
    chip: "bg-wg-teal",
    points: [
      "A more complete picture of your child's reading profile",
      "Identifies strengths and specific areas of need",
      "Informs intervention planning and home strategies",
    ],
  },
];

export default function Assessments() {
  return (
    <section
      id="assessments"
      data-testid="assessments-section"
      className="relative py-24 md:py-32 bg-wg-cream2/60"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-3.5 py-1.5 border border-wg-purple/15">
            <span className="w-1.5 h-1.5 rounded-full bg-wg-orange" />
            <span className="text-xs font-semibold tracking-[0.18em] uppercase text-wg-purple">Assessments</span>
          </div>
          <h2 className="mt-5 font-heading font-extrabold text-3xl md:text-5xl tracking-tight text-wg-ink leading-tight">
            Dyslexia Screeners and <span className="text-wg-teal">Core Assessments</span>
          </h2>
          <p className="mt-5 text-lg text-wg-ink2 max-w-2xl">
            Not sure which option is right for your child? Here's how they compare — and we'll help
            you choose during your free consultation.
          </p>
        </motion.div>

        <div className="mt-12 grid md:grid-cols-2 gap-6 lg:gap-8">
          {CARDS.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.article
                key={c.key}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.1 }}
                data-testid={`assessment-card-${c.key}`}
                className="relative rounded-[2rem] bg-white p-8 md:p-10 border border-wg-purple/10 shadow-[0_10px_30px_rgb(74,29,82,0.05)] hover:shadow-[0_22px_50px_rgb(74,29,82,0.10)] hover:-translate-y-1.5 transition-all duration-300 overflow-hidden"
              >
                <div className={`absolute -top-12 -right-12 w-40 h-40 rounded-full bg-gradient-to-br ${c.accent} opacity-20`} aria-hidden="true" />
                <div className={`relative inline-flex items-center justify-center w-14 h-14 rounded-2xl ${c.chip} text-white shadow-md`}>
                  <Icon className="w-7 h-7" />
                </div>
                <h3 className="relative mt-6 font-heading font-bold text-2xl md:text-3xl text-wg-ink">
                  {c.name}
                </h3>
                <ul className="relative mt-5 space-y-3">
                  {c.points.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-wg-ink2 text-[15px] leading-relaxed">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-wg-purple shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                <div className="relative mt-7">
                  <a
                    href={CALENDLY}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`assessment-${c.key}-cta`}
                    className="inline-flex items-center gap-2 bg-wg-purple text-white rounded-full px-6 py-3 font-semibold hover:bg-wg-purpleDeep hover:-translate-y-0.5 transition-all"
                  >
                    <CalendarCheck className="w-4 h-4" />
                    Book a Free Consultation
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
